import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { IFiasAddressPart } from '@interfaces/address.interface';
import { IMainLang } from '@interfaces/languages/main-lang';
import { InterfaceAccessMatrix, InterfacePermissionsMatrix } from '@modules/login/interfaces/login.interface';
import { HttpCacheService } from '@services/http-cache.service';
import { HttpOptionsService } from '@services/http-options.service';
import { LangService } from '@services/lang.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, take, tap } from 'rxjs/operators';
import { AppConfig } from './app.config';
import {
    IAppRequest,
    IAppServiceLanguages,
    IFiasPropsInterface,
    InterfaceAppServiceRegister,
    InterfaceAppSettings,
    IPublicMenuItem,
} from './app.interface';
import { AppSettings } from './core/libraries/Settings/app-settings';
import { TokenStorage } from './core/token.storage';
import { IMenuItem } from './layout/side-menu/interfaces/MenuItem.interface';
import { IBindCoordinates } from './modules/dynamic-fields/address/interface/address.interface';

/** Тип возможных настроек системы */
export type settingsType = InterfaceAppSettings | boolean | string;

/**
 * Сервис взаимодействия с внешними системами
 */
@Injectable()
export class AppService {
    /** Сервис для переключалки языков на формах */
    readonly #langService = inject(LangService);

    /** Карта кеширования запросов */
    readonly #httpCache = inject(HttpCacheService);

    /** Настройки системы */
    public appSettings: AppSettings;

    /** Флаг отображать меню для кнопки отображения меню (при маленьких экранах) */
    public showSideMenu: BehaviorSubject<boolean> = new BehaviorSubject(true);

    /** Наименование браузера */
    browseName: string = '';

    /**
     * Разрешить массовое редактирование
     */
    isMassEditAllowed = false;

    /**
     * URL через который идут все запросы
     */
    public serverUrl;

    /**
     * Объект переводов по языкам
     */
    public get language_dictionary(): IMainLang {
        return this._config.languageDictionary;
    }

    /**
     * Конфигурация сервиса системы
     */
    public get languages(): { [key: string]: IAppServiceLanguages } | null {
        return this.#langService.languages;
    }

    /**
     * Список названий всех доступных языков
     */
    public get languageNames(): string[] {
        return this.#langService.languageNames;
    }

    /**
     * Количество языков
     */
    public get languageCount(): number | null {
        return this.#langService.languageCount;
    }

    /**
     * Язык по умолчанию
     */
    public get defaultLanguage(): string {
        return this.#langService.defaultLanguage;
    }

    /**
     * Текущий язык
     */
    public get currentLanguage(): string {
        return this.#langService.currentLanguage;
    }

    /**
     * Код текущего языка
     */
    get currentLangCode(): string | null {
        return this.#langService.currentLangCode;
    }

    /**
     * Флаг авторизации в системе
     */
    public isAuth: boolean = false;

    /**
     * Настройки системы
     */
    public settings: { [key: string]: settingsType } = {};

    /**
     * Время для автосохранения в миллисекундах
     */
    public timeSave: number = 0;

    /**
     * Ограничение на скрытие поисковой строки в селекте
     */
    public hideSelectOptions: number;

    /**
     * Максимальный размер файла для автоматической загрузки
     */
    public maxSizeFileAutoUpload: number;

    /**
     * Максимальный размер файла для загрузки на сервер
     */
    public maxSizeFileUpload: number;

    /**
     * Карта ролей
     * @type {object|null}
     */
    public roleAccess: InterfacePermissionsMatrix;

    /**
     * Матрица доступов
     */
    public accessMatrix: InterfaceAccessMatrix;

    /**
     * Данные пользователя
     */
    public userData: any;

    /**
     * Адрес геокодеа
     */
    public urlGeocode = '';

    /**
     * Данные роута
     */
    public routeData: {
        title?: string;
        is_public?: boolean;
        link?: string;
        breadcrumbs?: { name?: string; link?: string; withoutTranslate?: boolean }[];
        header_button?: { name?: string; link?: string };
    } = null;

    /**
     * Текущая ссылка
     */
    public link: string = '';

    /**
     * Структура меню
     */
    menu: IMenuItem[] = null;

    /**
     * Открытие в новой вкладке
     */
    openNewTab: boolean = false;

    /**
     * URL карты
     */
    map = '/map/map_admin';

    /**
     * URL карточки
     */
    recordUrl = 'factor/input/row/';

    /**
     * Справочники
     */
    registers: {
        country?: InterfaceAppServiceRegister[]; // Страна
        city?: InterfaceAppServiceRegister[]; // Населенный пункт
    } = {
        country: [],
        city: [],
    };

    /**
     * Публичное меню
     */
    public publicMenu: IPublicMenuItem[];

    /**
     * Заголовок страницы из меню
     */
    public sideMenuTitle: string;

    /**
     * Публичная страница
     */
    private _isPublic: boolean = true;

    /** Сервис параметров http-запроса */
    protected _httpOptions: HttpOptionsService = inject(HttpOptionsService);
    public readonly http: HttpClient = inject(HttpClient);
    public readonly _config: AppConfig = inject(AppConfig);
    public readonly token: TokenStorage = inject(TokenStorage);

    constructor() {
        this.timeSave = this._config.timeSave;
        this.settings = this._config.settings;

        // eslint-disable-next-line rxjs-angular/prefer-takeuntil
        this._config.isAuth.subscribe((val) => {
            this.isAuth = val;
        });

        // eslint-disable-next-line rxjs-angular/prefer-takeuntil
        this.token.accessMatrix$.subscribe((newValue) => {
            this.accessMatrix = newValue;
        });

        // eslint-disable-next-line rxjs-angular/prefer-takeuntil
        this.token.roleAccess$.subscribe((newValue) => {
            this.roleAccess = newValue;
        });

        // eslint-disable-next-line rxjs-angular/prefer-takeuntil
        this.token.menu$.subscribe((newValue) => {
            this.menu = newValue;
        });

        // eslint-disable-next-line rxjs-angular/prefer-takeuntil
        this.token.currentLanguage$.subscribe((newValue) => {
            this.#langService.currentLanguage = newValue;
        });

        // eslint-disable-next-line rxjs-angular/prefer-takeuntil
        this.token.token$.subscribe((newValue) => {
            this.userData = newValue ? JSON.parse(newValue) : null;

            // Фото пользователя
            if (newValue && this._config.userData && this._config.userData.photo) {
                this.userData.photo = this._config.userData.photo ? this._config.userData.photo : null;
            }
            // Инициалы пользователя
            if (this?.userData) {
                this.userData.initials = this.userData?.firstname[0] + '' + this.userData?.lastname[0];
            }
        });

        this.urlGeocode = this._config.urlGeocode;
        this.hideSelectOptions = this._config.hideSelectOptions;
        this.maxSizeFileAutoUpload = this._config.maxSizeFileAutoUpload;
        this.maxSizeFileUpload = this._config.maxSizeFileUpload;
        this.serverUrl = this._config.url;
        this.appSettings = this._config.appSettings;
        this.settings = this._config.settings;
        this.publicMenu = this._config.publicMenu;
        this._setBrowseName();
    }

    /** Проверка имени браузера */
    protected _setBrowseName() {
        const userAgent = navigator.userAgent;
        if (userAgent.match(/chrome|chromium|crios/i)) {
            this.browseName = 'chrome';
        } else if (userAgent.match(/firefox|fxios/i)) {
            this.browseName = 'firefox';
        } else if (userAgent.match(/safari/i)) {
            this.browseName = 'safari';
        } else if (userAgent.match(/opr\//i)) {
            this.browseName = 'opera';
        } else if (userAgent.match(/edg/i)) {
            this.browseName = 'edge';
        } else {
            this.browseName = 'No browser detection';
        }
    }

    /**
     * Получение признака "Публичная страница"
     */
    get isPublic(): boolean {
        return this._isPublic;
    }

    /**
     * Установка признака "Публичная страница"
     * @param value значение
     */
    set isPublic(value: boolean) {
        this._isPublic = value;
    }

    /**
     * Получение одного справочника (без кэша)
     * @param name название справочника
     */
    public getRegister(name: string): Observable<any[]> {
        return this.http.post<IAppRequest>(this.serverUrl + '/registers', { scope: [name] }, this._httpOptions).pipe(
            take(1),
            map((result) => (result.data[name] ?? []) as any[]),
        );
    }

    /**
     * Получение справочников (с КЕШЕМ!)
     * @param scope набор справочника
     */
    public getRegisters(scope: string[]): Observable<any> {
        const headers = this._httpOptions.getCopyRequestHeaders({ 'use-cache': '' });
        return this.http.post<IAppRequest>(this.serverUrl + '/registers', { scope }, { headers });
    }

    /**
     * Получение списка имен рубрикаторов для показателя
     * @param factorId ИД показателя
     */
    public getRubricatorByFactor(factorId: number): Observable<IAppRequest<any[]>> {
        return this.http.post<IAppRequest<any[]>>(
            this.serverUrl + '/rubricator/front_rubricator/get_rubricator_list_for_node',
            { factor_id: factorId },
            this._httpOptions,
        );
    }

    /** Очистка кэша HTTP */
    public clearHttpCache(): void {
        this.#httpCache.clear();
    }

    /** Выход из системы */
    public logout(): Observable<any> {
        return this.http.post<any>(this.serverUrl + '/auth/front_auth/logout', {}, this._httpOptions).pipe(
            tap(() => {
                this.#httpCache.clear();
            }),
        );
    }

    /**
     * Скачивание файлов с сервера
     * @param data данные для запроса
     * @param url ссылка для запроса
     * @deprecated
     */
    downloadService(data: any, url: string): Observable<any> {
        const headers: {
            headers?:
                | HttpHeaders
                | {
                      [header: string]: string | string[];
                  };
            observe?: any;
            withCredentials?: boolean;
            responseType?: any;
        } = Object.assign({}, this._httpOptions);
        headers.withCredentials = true;
        headers.observe = 'response';
        headers.responseType = 'blob';
        return this.http.post(this.serverUrl + url, data, headers);
    }

    /**
     * Скачивание файла через открытие нового окна.
     * Необходимо для больших файлов, т.к. при загрузке их через фронт браузер зависает.
     *
     * @param url
     * @param postData
     */
    public downloadInNewWindow(url: string, postData: object) {
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = url;
        form.target = '_blank';

        const appendFormData: (data: any, parentKey: string) => void = (data, parentKey) => {
            if (Array.isArray(data)) {
                data.forEach((value, index) => {
                    appendFormData(value, `${parentKey}[${index}]`);
                });
            } else if (typeof data === 'object' && data !== null) {
                for (const key in data) {
                    if (data.hasOwnProperty(key)) {
                        const value = data[key];
                        const newKey = parentKey ? `${parentKey}[${key}]` : key;
                        appendFormData(value, newKey);
                    }
                }
            } else {
                const input = document.createElement('input');
                data = data === true ? 1 : data;
                data = data === false ? 0 : data;
                input.type = 'hidden';
                input.name = parentKey;
                input.value = data;
                form.appendChild(input);
            }
        };

        appendFormData(postData, '');

        document.body.appendChild(form);
        form.submit();
    }

    /**
     * Получение данных из фиаса
     */
    getFiasAddressByApi(data: IFiasPropsInterface): Observable<IAppRequest<IFiasAddressPart[]>> {
        return this.http.post<any>(this.serverUrl + '/fias/getData', data, this._httpOptions).pipe(take(1));
    }

    /**
     * Получение ФИАС
     * @param data данные
     */
    public getYandexGeocode(data: any): Observable<any> {
        let params = new HttpParams();
        for (const param in data) {
            if (data.hasOwnProperty(param) && data[param]) {
                params = params.append(param, data[param]);
            }
        }
        return this.http.jsonp(this.urlGeocode + `?${params.toString()}`, 'callback');
    }

    /**
     * Получение координат геообъекта по кадастровому номеру.
     * @param cadastralNumber Кадастровый номер
     * @param level Уровень ФИАС
     */
    public getCoordsByCN(cadastralNumber: string, level: string = '1'): Observable<any> {
        const actionUrl = this.getSettingsByKey('system_back_url') + '/map/map_action/getCoordsByCN';
        return this.http.get<any>(actionUrl + '?text=' + cadastralNumber + '&level=' + level);
    }

    /**
     * Открытие ссылки
     * @param url ссылка
     * @param target тип открытия ссылки
     */
    public openLink(url: string, target: string = '_blank') {
        window.open(url, target);
    }

    /**
     * Поиск строки адреса по слоям карты настроенных в методе
     * @param data данные
     */
    public findAddressByMethod(data: any): Observable<any> {
        return this.http.post<any>(
            this.serverUrl + '/factor/front_row/find_address_by_method',
            data,
            this._httpOptions,
        );
    }

    /**
     * Отправка запроса для вставки геометрии в формате WKT в запись реестра.
     * @param data Координаты
     * @param id Идентификатор записи реестра
     */
    bindCoordinates(data: IBindCoordinates, id: string) {
        return this.http.post<any>(
            this.serverUrl + this.map + '/copy_wkt_to_object',
            {
                id,
                wkt: 'POINT(' + data.x + ' ' + data.y + ')',
                epsg: data.epsg,
                is_object: data.is_object,
                source: data.source,
            },
            this._httpOptions,
        );
    }

    /**
     * Подписание файла на сервере
     * @param data данные
     */
    public saveFileSign(data: any): Observable<any> {
        return this.http.post<any>(this.serverUrl + '/files/front_files/sign', data, this._httpOptions);
    }

    /**
     * Получение последних уведомлений
     */
    getShortList(data: any): Observable<any> {
        return this.http.post<any>(
            this.serverUrl + '/notification/front_notification/short_list',
            data,
            this._httpOptions,
        );
    }

    /** Получить настройку по ключу */
    public getSettingsByKey(key: string): any {
        if (this.settings.hasOwnProperty(key)) {
            switch (typeof this.settings[key]) {
                case 'number':
                    return this.settings[key] ?? 0;
                case 'string':
                    return this.settings[key] ?? '';
                case 'boolean':
                    return this.settings[key];
                case 'object':
                    if (this.settings[key].hasOwnProperty('value')) {
                        const settings = this.settings[key] as InterfaceAppSettings;
                        return settings.value;
                    }
            }
        }
        return null;
    }

    /** Получить Favicon */
    public getFaviconPatch(): string {
        const favicon = this.settings.header_logo as any;
        return favicon?.file_name ?? '';
    }

    /** Получить лого системы */
    public getLoginWindowLogo(): string {
        const loginWindowLogo = this.settings.login_window_logo as any;
        return loginWindowLogo?.file_name ?? '';
    }

    /** Открыть страницу с руководством по разделу */
    public openHelpPage(url: string): void {
        this.openLink(this.settings.system_front_url + '/doc/' + url);
    }

    /** Открыть легаси конструктор реестров */
    public openFactorSettings(): void {
        this.openLink(this.settings.system_back_url + '/factor/index');
    }

    /** Открыть легаси конструктор связей */
    public openRelationSettings(): void {
        this.openLink(this.settings.system_back_url + '/designer_relations/index');
    }
}
